import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import PageHeading from "../../../Component/PageHeading";
import {InputText} from "primereact/inputtext";
import {IidName} from "../../../interfaces/interfaces";
import {useFetch} from "../../../layout/hooks/useFetch";
import Label from "../../../Component/Label";
import {Dropdown} from "primereact/dropdown";
import {baseUrl} from "../../../utils/webInfo";
import {LayoutContext} from "../../../layout/context/LayoutContext";
import callToast, {debounceFunction, mergeWithSelected} from "../../../utils/helper";
import {InputTextarea} from "primereact/inputtextarea";
import SmallHeader from "../../../Component/SmallHeader";
import {Image} from "primereact/image";
import {Button} from "primereact/button";
import {Divider} from "primereact/divider";
import {Toast} from "primereact/toast";
import {Dialog} from "primereact/dialog";

type Params = {
    id?: string;
}

const UpdateOrder = () => {
    let params = useParams<Params>();
    const navigate = useNavigate();
    const toast = useRef<Toast | null>(null);
    const {accessToken} = useContext(LayoutContext);

    // State declarations
    const [selectedReference, setSelectedReference] = useState<any>(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState<IidName | null>(null);
    const [selectedOrderStatus, setSelectedOrderStatus] = useState<IidName | null>(null);
    const [selectedDepot, setSelectedDepot] = useState<IidName | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [discount, setDiscount] = useState<number>(0);
    const [party, setParty] = useState<string>('');
    const [promoCode, setPromoCode] = useState<string>('');
    const [note, setNote] = useState<string>('');

    const [referenceUser, setReferenceUser] = useState<any>();
    const [paymentTypesData, setPaymentTypesData] = useState<IidName[]>([]);
    const [orderStatusData, setOrderStatusData] = useState<IidName[]>([]);
    const [depotData, setDepotData] = useState<IidName[]>([]);

    // filter
    const [filterReferenceUser, setFilterReferenceUser] = useState('');
    const [newProduct, setNewProduct] = useState<any>(null);
    const [productOption, setProductOption] = useState<any>([]);
    const [filterProduct, setFilterProduct] = useState('');
    const [visibleModal, setVisibleModal] = useState<boolean>(false);

    // Fetch order data
    const {data: orderInfo} = useFetch(`/api/order?filters=[["id",${params.id}]]`);
    // const {data: products} = useFetch(`/api/product?page=0&size=20&sort=-id${filterProduct ? `[["brandName","like","${filterProduct}"]]`: ""}`, []);
    const {data: products} = useFetch(`/api/product?page=0&size=30&sort=-id`,);

    useEffect(() => {
        if (products) {
            const modifiedData = products?.map((item: any) => {
                return ({
                    id: item.id,
                    quantity: 1,
                    total_price: item?.unitPriceMrp,
                    unit_price: item?.unitPriceMrp,
                    brandName: item?.brandName,
                    genericName: item?.genericName,
                    imageUrl: item?.imageUrl,
                    thumbnailImageUrl: item?.thumbnailImageUrl,
                    productId: item?.id,
                })
            })
            setProductOption(modifiedData);
        }
        else {
            setProductOption([])
        }
    }, [products]);

    // Calculate subtotal
    const subtotal = useMemo(() => {
        return selectedProducts?.reduce((sum, item) => sum + (item.total_price || 0), 0) || 0;
    }, [selectedProducts]);

    // Calculate total after discount
    const totalAfterDiscount = useMemo(() => {
        return subtotal - discount;
    }, [subtotal, discount]);

    // Update product quantity
    const updateProductQuantity = useCallback((productId: number, newQuantity: number) => {
        if (newQuantity < 1) return;

        setSelectedProducts(prevProducts =>
            prevProducts.map(item => {
                if (item.product_id === productId) {
                    const updatedItem = {
                        ...item,
                        quantity: newQuantity,
                        total_price: item.unit_price * newQuantity
                    };
                    return updatedItem;
                }
                return item;
            })
        );
    }, []);


    // Remove product from cart
    const removeProduct = useCallback((productId: number) => {
        setSelectedProducts(prevProducts =>
            prevProducts.filter(item => item.id !== productId)
        );
    }, []);

    // Handle discount change
    const handleDiscountChange = useCallback((e: any) => {
        setDiscount(e.target.value || 0);
    }, []);

    useEffect(() => {
        if (orderInfo) {
            const orderData = orderInfo[0];
            setNote(orderData?.note || '');
            setSelectedReference({
                id: orderData?.reference_user?.id,
                name: orderData?.reference_user?.name,
                unique_code: orderData?.reference_user?.system_employee_id,
                system_employee_id: orderData?.reference_user?.system_employee_id
            });
            setSelectedPaymentType(orderData?.payment_type ? {
                id: orderData?.payment_type?.id,
                name: orderData?.payment_type?.name
            } : null);
            setSelectedOrderStatus(orderData?.order_status ? {
                id: orderData?.order_status?.id,
                name: orderData?.order_status?.name
            } : null);
            setSelectedDepot(orderData?.depot ? {
                id: orderData?.depot.id,
                name: orderData?.depot.name
            } : null);
            setParty(orderData?.party_id || '');
            // setSelectedProducts(orderData?.products || []);
            setDiscount(orderData?.discount || 0);
            setPromoCode(orderData?.promo_code || '');
            const modifyProducts = orderData?.products.map((item: any) => {
                return {
                    id: item.id,
                    quantity: item.quantity,
                    total_price: item.total_price,
                    unit_price: item.unit_price,
                    brandName: item.product.brandName,
                    genericName: item.product.genericName,
                    imageUrl: item.product.imageUrl,
                    thumbnailImageUrl: item.product.thumbnailImageUrl,
                    productId: item.product_id,
                }
            })
            setSelectedProducts(modifyProducts);
        }
    }, [orderInfo]);

    // Fetch reference users
    const {
        data: referenceUserData,
        totalData: totalReference,
    } = useFetch(`/api/reference_user?page=0&size=10&sort=-id${filterReferenceUser ? `&filters=[["unique_code","like","${filterReferenceUser}"]]` : ""}`);

    // Fetch payment types
    useEffect(() => {
        if (!accessToken) return;

        const getData = async () => {
            try {
                const res = await fetch(`${baseUrl?.url}/api/payment_type?page=0&size=100`, {
                    method: "GET",
                    headers: {Authorization: `Bearer ${accessToken}`}
                });
                if (res.ok && res.status !== 204) {
                    const data = await res.json();
                    const option = data?.data?.items?.map((d: any) => ({
                        id: d.id, name: d.name
                    }));
                    setPaymentTypesData(option);
                }
            } catch (e) {
                console.error(e);
            }
        };
        getData().then();
    }, [accessToken]);

    // Fetch order status
    useEffect(() => {
        if (!accessToken) return;

        const getData = async () => {
            try {
                const res = await fetch(`${baseUrl?.url}/api/order_status?page=0&size=100`, {
                    method: "GET",
                    headers: {Authorization: `Bearer ${accessToken}`}
                });
                if (res.ok && res.status !== 204) {
                    const data = await res.json();
                    const option = data?.data?.items?.map((d: any) => ({
                        id: d.id, name: d.name
                    }));
                    setOrderStatusData(option);
                }
            } catch (e) {
                console.error(e);
            }
        };
        getData().then();
    }, [accessToken]);

    // Fetch depot data
    useEffect(() => {
        if (!accessToken) return;

        const getData = async () => {
            try {
                const res = await fetch(`${baseUrl?.url}/api/depot?page=0&size=100`, {
                    method: "GET",
                    headers: {Authorization: `Bearer ${accessToken}`}
                });
                if (res.ok && res.status !== 204) {
                    const data = await res.json();
                    const option = data?.data?.items?.map((d: any) => ({
                        id: d.id, name: d.name
                    }));
                    setDepotData(option);
                }
            } catch (e) {
                console.error(e);
            }
        };
        getData().then();
    }, [accessToken]);

    // modify ref
    useEffect(() => {
        if (referenceUserData) {
            const modifiedData = referenceUserData?.map((item: any) => ({
                id: item.id,
                name: item.name,
                unique_code: item.unique_code,
                system_employee_id: item.system_employee_id,
            }));
            setReferenceUser(mergeWithSelected(modifiedData, selectedReference));
        }
    }, [referenceUserData, selectedReference]);

    const handleSubmit = async () => {
        if (!params.id) return;

        const payload = {
            reference_user_id: selectedReference?.id,
            order_payment_type_id: selectedPaymentType?.id,
            order_status_id: selectedOrderStatus?.id,
            depot_id: selectedDepot?.id,
            party_id: party,
            discount,
            promo_code: promoCode,
            note,
            products: selectedProducts.map(p => ({
                product_id: p.productId,
                quantity: p.quantity,
                unit_price: p.unit_price,
                total_price: p.total_price
            }))
        }


        console.log("payload", payload);

        try {
            // setBtnLoading(true);
            const res = await fetch(`${baseUrl.url}/api/order/${params?.id}`, {
                method: "PUT",
                headers: {
                    "Content-type": "Application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload)
            });

            if (res.status === 304) {
                callToast(toast, "warn", "No data changed")
                return
            }
            if (res.ok) {
                callToast(toast, true, "Order updated successfully")
                navigate(-1)
            } else {
                const resData = await res.json();
                const {errors} = resData
                callToast(toast, false, errors[0]);
            }
        } catch (error: any) {
            callToast(toast, false, error?.message)
        } finally {
            // setBtnLoading(false)
        }

    };


    return (
        <div className="card">
            <PageHeading title={'Update Order'} back/>

            <div className="grid">
                <div className="col-12">
                    <div className="grid ">
                        {/* Left Column - Form Fields */}
                        <div className="col-12 md:col-6 lg:col-7 px-4">
                            <div className="grid">
                                {/* Reference User */}
                                <div className="col-12 md:col-6 lg:col-4">
                                    <Label label={"Reference User"} required={false}/>
                                    <Dropdown value={selectedReference}
                                              onFilter={(e) => {
                                                  const filterValue = e.filter;
                                                  if (totalReference > 20) {
                                                      const optimisedSearchHandler = debounceFunction(() => {
                                                          setFilterReferenceUser(filterValue);
                                                      }, 500);
                                                      optimisedSearchHandler();
                                                  }
                                              }}
                                              onChange={(e) => setSelectedReference(e.value)}
                                              options={referenceUser}
                                              optionLabel="name"
                                              itemTemplate={(option) => (
                                                  <div>
                                                      <p className="m-0">{option?.name} ({option?.unique_code})</p>
                                                  </div>
                                              )}
                                              valueTemplate={(option) => (
                                                  <div>
                                                      <p className="m-0">{option?.name} ({option?.unique_code})</p>
                                                  </div>
                                              )}
                                              filterPlaceholder="Search by Unique Code"
                                              placeholder="Select Reference User"
                                              className="w-full"
                                              showClear filter
                                              showFilterClear/>
                                </div>

                                {/* Payment Type */}
                                <div className="col-12 md:col-6 lg:col-4">
                                    <Label label={"Payment Type"} required={false}/>
                                    <Dropdown value={selectedPaymentType}
                                              onChange={(e) => setSelectedPaymentType(e.value)}
                                              options={paymentTypesData}
                                              optionLabel="name"
                                              placeholder="Select Payment Type"
                                              className="w-full"
                                              showClear/>
                                </div>

                                {/* Order Status */}
                                <div className="col-12 md:col-6 lg:col-4">
                                    <Label label={"Order Status"} required={false}/>
                                    <Dropdown value={selectedOrderStatus}
                                              onChange={(e) => setSelectedOrderStatus(e.value)}
                                              options={orderStatusData}
                                              optionLabel="name"
                                              placeholder="Select Order Status"
                                              className="w-full"
                                              showClear/>
                                </div>
                            </div>
                            <div className="grid">
                                {/* Depot */}
                                <div className="col-12 md:col-6 lg:col-4">
                                    <Label label={"Select Depot"} required={false}/>
                                    <Dropdown value={selectedDepot}
                                              onChange={(e) => setSelectedDepot(e.value)}
                                              options={depotData}
                                              optionLabel="name"
                                              placeholder="Select Depot"
                                              className="w-full"
                                              showClear/>
                                </div>

                                {/* Discount */}
                                <div className="col-12 md:col-6 lg:col-3">
                                    <Label label={"Discount %"} required={false}/>
                                    <InputText className="w-full"
                                               type="number"
                                               value={String(discount)}
                                               onChange={handleDiscountChange}
                                        // onValueChange={handleDiscountChange}
                                               placeholder="Discount"
                                    />
                                </div>

                                {/* Promo Code */}
                                <div className="col-12 md:col-6 lg:col-3">
                                    <Label label={"Promo"} required={false}/>
                                    <InputText className={'w-full'}
                                               value={promoCode}
                                               onChange={(e) => setPromoCode(e.target.value)}
                                               placeholder="Promo Code"/>
                                </div>
                            </div>
                            {/* Note */}
                            <div className="col-12 p-0">
                                <Label label={"Note"} required={false}/>
                                <InputTextarea className={'w-full'}
                                               value={note}
                                               onChange={(e) => setNote(e.target.value)}
                                               placeholder="Note"
                                               rows={3}/>
                            </div>
                        </div>

                        {/* Right Column - Products and Summary */}
                        <div className="col-12 md:col-6 lg:col-5 px-4">
                            <div className="flex justify-content-between align-items-center">
                                <SmallHeader text={'Products'}/>
                                <Button icon="pi pi-plus" onClick={() => {
                                    setVisibleModal(true)
                                }} rounded text aria-label="add product"/>
                            </div>

                            {selectedProducts?.length > 0 ? (
                                <>
                                    {/* Products List */}
                                    <div className="product-list border-bottom-1 border-gray-300"
                                         style={{maxHeight: '400px', overflowY: 'auto'}}>
                                        {selectedProducts?.map((item: any, index: number) => (
                                            <div key={index} className="col-12">
                                                <div
                                                    className="flex gap-2 w-full align-items-center border border-round border-primary">
                                                    <Image src="/default.png" alt="Product" width="80"/>
                                                    <div
                                                        className="flex justify-content-between align-items-center w-full">
                                                        <div className="flex-1">
                                                            <p className="font-semibold m-0">{item?.brandName}</p>
                                                            <p className="m-0 text-sm text-color-secondary">{item?.genericName}</p>
                                                            <div className="flex align-items-center gap-2 mt-2">
                                                                <Button icon="pi pi-minus"
                                                                        className="p-button-rounded p-button-text p-button-sm"
                                                                        onClick={() => updateProductQuantity(item.product_id, item.quantity - 1)}
                                                                        disabled={item.quantity <= 1}/>
                                                                <span
                                                                    className="font-semibold w-2rem text-center">{item.quantity}</span>
                                                                <Button icon="pi pi-plus"
                                                                        className="p-button-rounded p-button-text p-button-sm"
                                                                        onClick={() => updateProductQuantity(item.product_id, item.quantity + 1)}/>
                                                                <Button icon="pi pi-trash"
                                                                        className="p-button-rounded p-button-text p-button-danger p-button-sm ml-2"
                                                                        onClick={() => removeProduct(item.id)}
                                                                        tooltip="Remove item"/>
                                                            </div>
                                                        </div>

                                                        <div className="text-right">
                                                            <p className="font-semibold m-0 text-primary">৳{item.unit_price}</p>
                                                            <p className="font-semibold m-0 text-lg">৳{item.total_price}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>


                                    {/* Order Summary */}
                                    <div className="summary-section p-3">
                                        <div className="flex justify-content-between mb-2">
                                            <span className="text-color-secondary">Subtotal:</span>
                                            <span className="font-bold">৳{subtotal.toFixed(2)}</span>
                                        </div>

                                        {discount > 0 && (
                                            <div className="flex justify-content-between mb-2">
                                                <span className="text-color-secondary">Discount:</span>
                                                <span className="font-bold text-green-600">-৳{discount}</span>
                                            </div>
                                        )}
                                        <Divider className="my-2"/>
                                        <div className="flex justify-content-between mb-3">
                                            <span className="text-xl font-bold">Total:</span>
                                            <span
                                                className="text-xl font-bold text-primary">৳{(totalAfterDiscount).toFixed(2)}</span>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-2 justify-content-start">
                                            <Button label="Update Order"
                                                    onClick={handleSubmit}/>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="pi pi-shopping-cart text-5xl text-color-secondary mb-3"></i>
                                    <h3 className="text-xl text-color-secondary">No Products Found</h3>
                                    <p className="text-color-secondary mb-3">This order has no products</p>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>


            <Dialog
                visible={visibleModal}
                headerClassName="text-800 font-semibold text-xl"
                header={`Add a New Product`}
                className={"w-11 sm:w-10 lg:w-5 lg:w-4 xl:w-3"}
                onHide={() => {
                    setVisibleModal(false)
                }}
            >
                <div className={""}>
                    <div className={"field"}>
                        <Label label={"Product"} required={true}/>
                        <Dropdown value={newProduct}
                                  onChange={(e) => setNewProduct(e.value)}
                                  options={productOption}
                                  optionLabel="brandName"
                                  placeholder="Select Product"
                                  className="w-full"
                                  showClear
                                  filter
                                  onFilter={(e) => {
                                      const filterValue = e.filter;
                                      if (totalReference > 20) {
                                          const optimisedSearchHandler = debounceFunction(() => {
                                              setFilterProduct(filterValue);
                                          }, 500);
                                          optimisedSearchHandler();
                                      }
                                  }}
                        />
                    </div>


                    <Button
                        label={'Add Product'}
                        className={"mt-3"}
                        onClick={() => {
                            const addNewProduct = [...selectedProducts, newProduct]
                            setSelectedProducts(addNewProduct)
                            setNewProduct(null)
                            setVisibleModal(false)
                        }}

                    />
                </div>
            </Dialog>
        </div>
    );
};

export default UpdateOrder;